/**
 * src/components/pipeline/quote/copilot/CopilotShellParts.jsx — TICKET-912 slice 1.
 *
 * The pieces the floating bot (AgentShell) renders in Quote Copilot mode:
 *   CopilotModeChip   — header chip: "Quote Copilot · <deal>" <-> "General chat"
 *   CopilotSuggestions — empty-thread quoting prompts (tap = send)
 *   CopilotFillAction — the one-tap "fill every bundle + raise the questions" turn,
 *                       always available once the thread has turns (TICKET-912 slice 4)
 *   CopilotItemsPane  — the deal's open items, in the sidebar/drawer slot
 * Tokens only; every control >= 44px.
 */

import { MessagesSquare, MessageCircle, Sparkles } from 'lucide-react';
import { useT } from '../../../../lib/i18n/I18nContext';
import { OpenItemsList } from './CopilotBits';

const chip = (active) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 44, padding: '0 12px', borderRadius: 22,
  border: 'none', outline: `1px solid ${active ? 'var(--accent)' : 'var(--bdr)'}`,
  background: active ? 'var(--accent-bg)' : 'var(--card)', color: active ? 'var(--accent)' : 'var(--t2)',
  // Shrinks with an ellipsis: a long deal name must never push the close button off a 375px header.
  fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 1, minWidth: 0, maxWidth: '100%',
});

export function CopilotModeChip({ on, dealName, onToggle }) {
  const { t } = useT();
  const label = on
    ? `${t('quoteCopilot.panelTitle')}${dealName ? ` · ${dealName}` : ''}`
    : t('quoteCopilot.panelTitle');
  return (
    <button type="button" data-testid="copilot-mode-chip" aria-pressed={on} onClick={onToggle} style={chip(on)}
      title={on ? t('quoteCopilot.switchToGeneral') : t('quoteCopilot.switchToCopilot')}>
      {on ? <MessagesSquare size={16} aria-hidden="true" /> : <MessageCircle size={16} aria-hidden="true" />}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
    </button>
  );
}

const SUGGESTION_KEYS = ['suggestFillBundles', 'suggestMissing', 'suggestQuestions', 'suggestLines'];

/** One tap = the same turn as the empty-thread "fill every bundle" suggestion. */
export function CopilotFillAction({ onSend, disabled }) {
  const { t } = useT();
  return (
    <button type="button" data-testid="copilot-fill-all" disabled={disabled} onClick={() => onSend(t('quoteCopilot.suggestFillBundles'))}
      title={t('quoteCopilot.suggestFillBundles')} style={{ ...chip(false), opacity: disabled ? 0.5 : 1, cursor: disabled ? 'default' : 'pointer' }}>
      <Sparkles size={16} color="var(--accent)" aria-hidden="true" style={{ flexShrink: 0 }} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t('quoteCopilot.fillAllAction')}</span>
    </button>
  );
}

export function CopilotSuggestions({ onSend }) {
  const { t } = useT();
  return (
    <div data-testid="copilot-suggestions" style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '20px 16px', maxWidth: 640, margin: '0 auto', width: '100%' }}>
      <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: 'var(--tm)' }}>{t('quoteCopilot.emptyThread')}</p>
      {SUGGESTION_KEYS.map((k) => (
        <button key={k} type="button" data-testid={`copilot-suggestion-${k}`} onClick={() => onSend(t(`quoteCopilot.${k}`))}
          style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, padding: '10px 14px', borderRadius: 12, border: 'none', outline: '1px solid var(--bdr)', background: 'var(--card)', color: 'var(--t1)', fontSize: 14, textAlign: 'left', cursor: 'pointer' }}>
          <Sparkles size={16} color="var(--accent)" aria-hidden="true" style={{ flexShrink: 0 }} />
          <span>{t(`quoteCopilot.${k}`)}</span>
        </button>
      ))}
    </div>
  );
}

export function CopilotItemsPane({ items, error, onCopy, onClose }) {
  const { t } = useT();
  const openCount = (items || []).filter((it) => it.status === 'open').length;
  return (
    <div data-testid="copilot-items-pane" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: 'var(--bg)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', borderBottom: '1px solid var(--bdr)', minHeight: 44 }}>
        <strong style={{ fontSize: 14, color: 'var(--t1)' }}>
          {t('quoteCopilot.itemsTab')}
          {openCount > 0 && <span data-testid="copilot-items-count" style={{ marginLeft: 4, color: 'var(--warn)' }}>({openCount})</span>}
        </strong>
        {onClose && (
          <button type="button" onClick={onClose} data-testid="copilot-items-close" aria-label={t('common.close')}
            style={{ minWidth: 44, minHeight: 44, border: 'none', background: 'transparent', color: 'var(--tm)', cursor: 'pointer' }}>×</button>
        )}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px' }}>
        <OpenItemsList items={items} error={error} onCopy={onCopy} />
      </div>
    </div>
  );
}
